import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, Typography } from '@/components';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Mail, TrendingUp, Activity, Loader2 } from 'lucide-react';

import { useEmailPreferences } from '@/features/users/hooks/use-email-preferences';
import { EmailPreferenceField } from '@/features/users/api/types';

export type EmailNotificationsFormProps = {
  readonly userId: string;
};

export function EmailNotificationsForm({ userId }: EmailNotificationsFormProps) {
  const { t } = useTranslation(['settings']);

  const { preferences, isLoading, isInitializing, updatingField, updatePreference } = useEmailPreferences({
    userId,
    showSuccessToast: true,
    showErrorToast: true,
  });

  const handleToggle = useCallback(
    (field: EmailPreferenceField) => async (checked: boolean) => {
      try {
        await updatePreference(field, checked);
      } catch (error) {
        console.error('Preferences toggle error:', error);
      }
    },
    [updatePreference]
  );

  if (isLoading || isInitializing) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="rounded-lg bg-yellow-50 p-4">
        <p className="text-sm text-yellow-800">{t('Could not load email preferences. Please refresh the page.')}</p>
      </div>
    );
  }

  const emailSettings = [
    {
      id: 'recommendations_by_interests' as const,
      icon: TrendingUp,
      title: t('Recommendations by interests'),
      description: (
        <>
          {t('Receive personalized suggestions based on your preferences and interests')}.{' '}
          <Link
            to="/settings/company"
            className="text-primary inline-flex items-center gap-1 font-medium hover:underline"
          >
            {t('Edit your Company Interests here')}
          </Link>
        </>
      ),
      checked: preferences.recommendationsByInterests,
    },
    {
      id: 'recommendations_by_activity' as const,
      icon: Activity,
      title: t('Recommendations by activity'),
      description: t('Receive content related to your history and behavior on the platform'),
      checked: preferences.recommendationsByActivity,
    },
    {
      id: 'newsletter_mkt' as const,
      icon: Mail,
      title: t('Newsletter'),
      description: t('Receive our newsletter with news, updates and featured content'),
      checked: preferences.newsletterMkt,
    },
  ];

  return (
    <div className="space-y-6">
      <Typography as="h3" color="primary" size="title" weight="semibold">
        {t('Email notifications')}
      </Typography>

      <div className="space-y-3">
        {emailSettings.map((setting) => {
          const Icon = setting.icon;
          const isUpdating = updatingField === setting.id;

          return (
            <div
              key={setting.id}
              className="flex flex-col space-y-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50 sm:flex-row sm:items-start sm:space-y-0 sm:space-x-4"
            >
              <div className="bg-primary/10 mt-0.5 hidden shrink-0 rounded-md p-2 sm:block">
                <Icon className="text-primary h-5 w-5" />
              </div>

              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-start justify-between gap-3 sm:items-center">
                  <div className="flex items-center gap-2 sm:hidden">
                    <div className="bg-primary/10 rounded-md p-2">
                      <Icon className="text-primary h-4 w-4" />
                    </div>
                    <Label htmlFor={setting.id} className="cursor-pointer text-sm leading-none font-medium">
                      {setting.title}
                    </Label>
                  </div>

                  <Label
                    htmlFor={setting.id}
                    className="hidden cursor-pointer text-sm leading-none font-medium sm:block"
                  >
                    {setting.title}
                  </Label>

                  <div className="flex shrink-0 items-center space-x-2">
                    {isUpdating && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                    <Switch
                      id={setting.id}
                      checked={setting.checked}
                      onCheckedChange={handleToggle(setting.id)}
                      disabled={isUpdating}
                      className={'hover:cursor-pointer'}
                    />
                  </div>
                </div>

                <p className="text-sm leading-relaxed text-gray-600">{setting.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg bg-gray-100 p-2">
        <Typography size="caption">
          {t('You can change these preferences at any time. Changes apply immediately.')}
        </Typography>
      </div>
    </div>
  );
}
